import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../lib/api';
import type {
  Client,
  ClientListItem,
  ClientDetailBundle,
  ClientActivity,
  ClientNote,
  Paged,
} from '@repo/shared-types';
import type { CreateClientInput, UpdateClientInput } from '@repo/validation';
import { toast } from 'sonner';

interface ListOpts {
  search?: string;
  tag?: string;
  sort?: 'newest' | 'oldest' | 'name';
  page?: number;
  pageSize?: number;
}

function buildClientsUrl(opts?: ListOpts): string {
  const params = new URLSearchParams();
  if (opts?.search) params.set('search', opts.search);
  if (opts?.tag) params.set('tag', opts.tag);
  if (opts?.sort) params.set('sort', opts.sort);
  if (opts?.page) params.set('page', String(opts.page));
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize));
  const qs = params.toString();
  return qs ? `/clients?${qs}` : '/clients';
}

export function useClients(opts?: ListOpts) {
  return useQuery<Paged<ClientListItem>>({
    queryKey: [
      'clients',
      {
        search: opts?.search ?? null,
        tag: opts?.tag ?? null,
        sort: opts?.sort ?? null,
        page: opts?.page ?? null,
        pageSize: opts?.pageSize ?? null,
      },
    ],
    queryFn: () => fetchApi(buildClientsUrl(opts)),
  });
}

/** Dropdown consumers (payer pickers) just want a flat list, not a page —
 * pageSize:200 (MAX_PAGE_SIZE)/sort:name keeps it complete-enough and
 * alphabetical without dragging pagination UI into every drawer/form that
 * needs "pick a client". Still a hard cap, not a real fix at scale — a
 * searchable combobox that queries the server is the right long-term shape
 * for the payer picker once an org has more clients than this covers. */
export function useClientOptions() {
  const { data, ...rest } = useClients({ pageSize: 200, sort: 'name' });
  return { data: data?.items ?? [], ...rest };
}

export function useClientTags() {
  return useQuery<string[]>({
    queryKey: ['clients', 'tags'],
    queryFn: () => fetchApi('/clients/tags'),
  });
}

export function useClient(id: string | null) {
  return useQuery<Client>({
    queryKey: ['clients', id],
    queryFn: () => fetchApi(`/clients/${id}`),
    enabled: id !== null,
  });
}

export function useClientDetail(id: string | null) {
  return useQuery<ClientDetailBundle>({
    queryKey: ['clients', id, 'detail'],
    queryFn: () => fetchApi(`/clients/${id}/detail`),
    enabled: id !== null,
  });
}

/**
 * Dup-payer soft-warn lookup (Task 7/8, I-billing-safety-audit): given a
 * zaloPhone, returns the existing org client with that phone (or null).
 * Non-blocking — callers use this to warn, not to prevent, creation.
 * Pass '' (or only set the phone once the field loses focus) to avoid
 * firing on every keystroke.
 */
export function useClientLookup(zaloPhone: string) {
  const trimmed = zaloPhone.trim();
  return useQuery<{ id: string; name: string } | null>({
    queryKey: ['clients', 'lookup', trimmed],
    queryFn: () => fetchApi(`/clients/lookup?zaloPhone=${encodeURIComponent(trimmed)}`),
    enabled: trimmed.length > 0,
    staleTime: 30_000,
  });
}

interface ActivitiesOpts {
  limit?: number;
  before?: string;
}

export function useClientActivities(id: string | null, opts?: ActivitiesOpts) {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.before) params.set('before', opts.before);
  const qs = params.toString();
  const url = qs ? `/clients/${id}/activities?${qs}` : `/clients/${id}/activities`;

  return useQuery<ClientActivity[]>({
    queryKey: ['clients', id, 'activities', { limit: opts?.limit ?? null, before: opts?.before ?? null }],
    queryFn: () => fetchApi(url),
    enabled: id !== null,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClientInput) =>
      fetchApi<Client>('/clients', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Đã tạo khách hàng');
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể tạo khách hàng');
    },
  });
}

export function useUpdateClient(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateClientInput) =>
      fetchApi<Client>(`/clients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients', id] });
      queryClient.invalidateQueries({ queryKey: ['clients', id, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể cập nhật khách hàng');
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ deleted: boolean }>(`/clients/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Đã xoá khách hàng');
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể xoá khách hàng');
    },
  });
}

interface CreateNoteVars {
  clientId: string;
  content: string;
}

export function useCreateClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, content }: CreateNoteVars) =>
      fetchApi<ClientNote>(`/clients/${clientId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ content }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể thêm ghi chú');
    },
  });
}

interface UpdateNoteVars {
  clientId: string;
  noteId: string;
  content: string;
}

export function useUpdateClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId, content }: UpdateNoteVars) =>
      fetchApi<ClientNote>(`/clients/${clientId}/notes/${noteId}`, {
        method: 'PUT',
        body: JSON.stringify({ content }),
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể cập nhật ghi chú');
    },
  });
}

interface DeleteNoteVars {
  clientId: string;
  noteId: string;
}

export function useDeleteClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId }: DeleteNoteVars) =>
      fetchApi<{ deleted: boolean }>(`/clients/${clientId}/notes/${noteId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients', variables.clientId, 'detail'] });
    },
    onError: (error) => {
      toast.error(error.message || 'Không thể xoá ghi chú');
    },
  });
}
